import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const VerticalCutReveal = ({
  children,
  splitBy = 'characters',
  staggerDuration = 0.025,
  staggerFrom = 'first',
  transition = {
    type: 'spring',
    damping: 30,
    stiffness: 200,
  },
  containerClassName = '',
  lineClassName = '',
  charClassName = '',
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const text = typeof children === 'string' ? children : '';

  const getDelay = (index, total) => {
    if (staggerFrom === 'center') {
      const center = total / 2;
      return Math.abs(index - center) * staggerDuration;
    }
    if (staggerFrom === 'last') {
      return (total - 1 - index) * staggerDuration;
    }
    return index * staggerDuration;
  };

  if (splitBy === 'characters') {
    // Group characters by words to prevent mid-word line breaks
    const words = text.split(' ');
    let charIndex = 0;
    const totalChars = text.replace(/ /g, '').length + words.length - 1;

    return (
      <span ref={ref} className={`inline-flex flex-wrap ${containerClassName}`}>
        {words.map((word, wIdx) => (
          <span key={wIdx} className="inline-flex whitespace-nowrap">
            {word.split('').map((ch, cIdx) => {
              const currentIdx = charIndex++;
              return (
                <span key={currentIdx} className={`inline-block overflow-hidden ${charClassName}`}>
                  <motion.span
                    className={`inline-block ${lineClassName}`}
                    initial={{ y: '110%', opacity: 0 }}
                    animate={isInView ? { y: '0%', opacity: 1 } : { y: '110%', opacity: 0 }}
                    transition={{
                      ...transition,
                      delay: getDelay(currentIdx, totalChars),
                    }}
                  >
                    {ch}
                  </motion.span>
                </span>
              );
            })}
            {wIdx < words.length - 1 && (
              <span className="inline-block overflow-hidden">
                <motion.span
                  className={`inline-block ${lineClassName}`}
                  initial={{ y: '110%', opacity: 0 }}
                  animate={isInView ? { y: '0%', opacity: 1 } : { y: '110%', opacity: 0 }}
                  transition={{
                    ...transition,
                    delay: getDelay(charIndex++, totalChars),
                  }}
                >
                  {'\u00A0'}
                </motion.span>
              </span>
            )}
          </span>
        ))}
      </span>
    );
  }

  // Word-based splitting
  const words = text.split(' ');
  return (
    <span ref={ref} className={`inline-flex flex-wrap ${containerClassName}`}>
      {words.map((word, i) => (
        <span key={i} className={`inline-block overflow-hidden mr-[0.3em] ${charClassName}`}>
          <motion.span
            className={`inline-block ${lineClassName}`}
            initial={{ y: '110%', opacity: 0 }}
            animate={isInView ? { y: '0%', opacity: 1 } : { y: '110%', opacity: 0 }}
            transition={{
              ...transition,
              delay: getDelay(i, words.length),
            }}
          >
            {word}
          </motion.span>
        </span>
      ))}
    </span>
  );
};

export default VerticalCutReveal;
